import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function AboutPage() {
  const { getTotalItems } = useCart();

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e7c59d1ae2a34bd109398a277a5c5542.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-yellow-400">GameVault</span>
            </h1>
            <p className="text-xl text-purple-200 mb-8 max-w-3xl mx-auto">
              Your trusted partner for digital gaming products. We've been delivering instant gaming experiences to millions of players worldwide since 2018.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <p className="text-gray-300 mb-6">
                GameVault was founded by passionate gamers who understood the frustration of waiting for game codes and dealing with unreliable sellers. We set out to create the most trusted and efficient digital gaming marketplace in the industry.
              </p>
              <p className="text-gray-300 mb-6">
                Starting with just a handful of popular games, we've grown to become one of the leading platforms for digital gaming products, serving over 2 million customers across 150 countries.
              </p>
              <p className="text-gray-300">
                Today, we specialize in providing instant access to the most popular gaming content, with a particular focus on Fortnite and Epic Games products, ensuring our customers get the best prices and fastest delivery times.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20gaming%20team%20working%20together%20in%20modern%20office%20environment%20with%20multiple%20monitors%20gaming%20setup%20collaborative%20workspace%20technology%20focused%20atmosphere%20purple%20accent%20lighting&width=600&height=400&seq=about-team&orientation=landscape"
                alt="Our team at work"
                className="rounded-lg object-cover object-top w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              To make digital gaming accessible, affordable, and instant for every gamer around the world.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-3">Innovation</h3>
              <p className="text-gray-400">
                We continuously innovate our platform to provide the fastest and most secure digital delivery experience.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-3">Trust</h3>
              <p className="text-gray-400">
                Every transaction is protected with industry-leading security measures and backed by our guarantee.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-3">Support</h3>
              <p className="text-gray-400">
                Our dedicated support team is available 24/7 to ensure you have the best gaming experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">2M+</div>
              <div className="text-gray-300">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">150+</div>
              <div className="text-gray-300">Countries Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">99.9%</div>
              <div className="text-gray-300">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">&lt;30s</div>
              <div className="text-gray-300">Average Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">Meet Our Team</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our diverse team of gaming enthusiasts, developers, and customer success specialists work around the clock to bring you the best gaming marketplace experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20male%20CEO%20gaming%20industry%20executive%20portrait%20confident%20leader%20modern%20office%20background%20business%20attire%20technology%20focused&width=300&height=300&seq=ceo&orientation=squarish"
                alt="CEO"
                className="w-32 h-32 rounded-full object-cover object-top mx-auto mb-4"
              />
              <h3 className="text-white font-semibold text-lg">Alex Johnson</h3>
              <p className="text-purple-400 mb-2">CEO & Founder</p>
              <p className="text-gray-400 text-sm">
                Former Epic Games executive with 15+ years in the gaming industry.
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20female%20CTO%20technology%20leader%20gaming%20industry%20portrait%20confident%20developer%20modern%20office%20background%20business%20attire&width=300&height=300&seq=cto&orientation=squarish"
                alt="CTO"
                className="w-32 h-32 rounded-full object-cover object-top mx-auto mb-4"
              />
              <h3 className="text-white font-semibold text-lg">Sarah Chen</h3>
              <p className="text-purple-400 mb-2">CTO</p>
              <p className="text-gray-400 text-sm">
                Tech visionary who built our secure and scalable platform infrastructure.
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20male%20customer%20success%20manager%20gaming%20industry%20portrait%20friendly%20approachable%20modern%20office%20background%20business%20attire&width=300&height=300&seq=csm&orientation=squarish"
                alt="Customer Success Manager"
                className="w-32 h-32 rounded-full object-cover object-top mx-auto mb-4"
              />
              <h3 className="text-white font-semibold text-lg">Mike Rodriguez</h3>
              <p className="text-purple-400 mb-2">Head of Customer Success</p>
              <p className="text-gray-400 text-sm">
                Ensures every customer has an amazing experience with our platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Level Up?</h2>
          <p className="text-gray-300 mb-8">
            Join millions of gamers who trust GameVault for their digital gaming needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/" className="inline-block">
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-shopping-cart-line mr-2"></i>
                Start Shopping
              </button>
            </a>
            <a href="/contact" className="inline-block">
              <button className="border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-customer-service-line mr-2"></i>
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}